/**
 * Fork of @fnando/sparkline
 */

export interface SparklineResultCommands {
    fillCoords: string;
    pathCoords: string;
    tickCoords: string;
}

export interface SparklineResultStats {
    valuesMax: number;
    valuesMin: number;
    totalMax: number;
    totalMin: number;
}

export interface SparklineResult {
    commands: SparklineResultCommands;
    stats: SparklineResultStats;
}

const getY = (min: number, max: number, height: number, diff: number, value: number): number =>
    max !== min
        ? parseFloat((height - ((value - min) * height / (max - min)) + diff).toFixed(2))
        : parseFloat((height - (height / 2) + diff).toFixed(2));

const getMin = (value1: number, value2: number): number =>
    (value1 || value1 === 0) && (value2 || value2 === 0)
        ? value1 > value2 ? value2 : value1
        : (value1 || value1 === 0) ? value1 : value2;

const getMax = (value1: number, value2: number): number =>
    (value1 || value1 === 0) && (value2 || value2 === 0)
        ? value1 < value2 ? value2 : value1
        : (value1 || value1 === 0) ? value1 : value2;

export const sparkline = (
    values: number[],
    tick: number | undefined,
    options: {
        spotRadius?: number;
        width: number;
        height: number;
        strokeWidth: number;
        applyGradient?: boolean;
    },
    prevResult?: SparklineResult
): SparklineResult => {
    let commands: SparklineResultCommands;
    let stats: SparklineResultStats;

    if (!values?.length || values.length === 1) {
        // Draw a straight line
        const spotRadius = options.spotRadius || 2;
        const spotDiameter = spotRadius * 2;
        const strokeWidth = options.strokeWidth;
        const width = options.width - spotDiameter * 2;
        const fullHeight = options.height;
        const height = fullHeight - (strokeWidth * 2) - spotDiameter;
        const singleValue = values?.[0] || 0;
        const y = getY(singleValue, singleValue, height, strokeWidth + spotRadius, singleValue);
        const pathCoords = `M${spotDiameter} ${y} H${width + spotDiameter}`;
        const fillCoords = `${pathCoords} V${fullHeight} L${spotDiameter} ${fullHeight} Z`;
        commands = {pathCoords, fillCoords, tickCoords: ''};
        stats = {valuesMin: singleValue, valuesMax: singleValue, totalMin: singleValue, totalMax: singleValue};
    } else {
        // Define how big should be the spot area.
        const spotRadius = options.spotRadius || 2;
        const spotDiameter = spotRadius * 2;

        // Get the stroke width; this is used to compute the
        // rendering offset.
        const strokeWidth = options.strokeWidth;

        // The rendering width will account for the spot size.
        const width = options.width - spotDiameter * 2;

        // Get the SVG element's full height.
        // This is used
        const fullHeight = options.height;

        // The rendering height accounts for stroke width and spot size.
        const height = fullHeight - (strokeWidth * 2) - spotDiameter;

        if (!prevResult) {
            stats = values.reduce<SparklineResultStats>(
                (acc, value) => {
                    acc.valuesMin = getMin(acc.valuesMin, value);
                    acc.valuesMax = getMax(acc.valuesMax, value);

                    return acc;
                },
                {
                    valuesMin: undefined,
                    valuesMax: undefined,
                    totalMin:  undefined,
                    totalMax:  undefined
                }
            );
        } else {
            stats = prevResult.stats;
        }

        stats.totalMin = getMin(stats.valuesMin, tick);
        const min = stats.totalMin;

        stats.totalMax = getMax(stats.valuesMax, tick);

        // Don't allow it to be zero, because it's used as divider.
        const max = stats.totalMax || 1;

        const isScaleChanged = !prevResult
            || prevResult.stats.totalMin !== stats.totalMin
            || prevResult.stats.totalMax !== stats.totalMax;

        if (isScaleChanged) {
            // Cache the last item index.
            const lastItemIndex = values.length - 1;

            // Calculate the X coord base step.
            const offset = width / lastItemIndex;

            // Hold the line coordinates.
            const pathY = getY(min, max, height, strokeWidth + spotRadius, values[0]);
            let pathCoords = `M${spotDiameter} ${pathY}`;

            if (min !== max) {
                values.forEach((value, index) => {
                    const x = parseFloat((index * offset + spotDiameter).toFixed(2));
                    const y = getY(min, max, height, strokeWidth + spotRadius, value);

                    pathCoords += ` L${x} ${y}`;
                });
            } else {
                pathCoords += ` H${width}`;
            }

            const fillCoords = `${pathCoords} V${fullHeight} L${spotDiameter} ${fullHeight} Z`;

            commands = {pathCoords, fillCoords, tickCoords: ''};
        } else {
            commands = prevResult.commands;
        }

        let tickCoords;

        if (tick || tick === 0) {
            const tickY = getY(min, max, height, strokeWidth + spotRadius, tick);

            tickCoords = `M0 ${tickY} H${options.width}`;

            // Adding a segment from the last chart point to the end of the tick line
            if (values.length > 0) {
                commands.pathCoords += ` L${options.width} ${tickY}`;
            }
        } else {
            tickCoords = '';
        }

        commands.tickCoords = tickCoords;
    }

    return {
        commands,
        stats
    };
};
